import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import {
  enterpriseAdminService,
  ProgramSummary,
  MarketSummary,
} from '../services/enterpriseAdminService';
import { useToast } from './useToast';
import { useEnterprise } from '../contexts/EnterpriseContext';

// Transform raw market data to computed format once
const transformMarketData = (markets: any[], currentMarketsLength: number) => {
  return markets.map((market, index) => ({
    id: index + 1 + currentMarketsLength,
    city: market.market,
    state: market.state,
    country: market.country,
    countryCode: market.country_code,
    preferredProperties: market.preferred_properties,
    scoredProperties: market.scored_hotels,
    marketScore: market.avg_alo_score,
    scoreE: market.avg_e_score,
    scoreS: market.avg_s_score,
    scoreG: market.avg_g_score,
    marketScoreFromHotel: market.avg_alo_score_from_hotel,
    scoreEFromHotel: market.avg_e_score_from_hotel,
    scoreSFromHotel: market.avg_s_score_from_hotel,
    scoreGFromHotel: market.avg_g_score_from_hotel,
    propertiesBooked: market.total_hotels,
    nightsBooked: market.preferred_room_nights,
    // Detailed ESG scores
    avgEBiodiversity: market.avg_e_biodiversity,
    avgEClimate: market.avg_e_climate,
    avgEEnergy: market.avg_e_energy,
    avgEWaste: market.avg_e_waste,
    avgEWater: market.avg_e_water,
    avgSAccessibility: market.avg_s_accessibility,
    avgSCommunity: market.avg_s_community,
    avgSHumanRights: market.avg_s_human_rights,
    avgSResponsibleSourcing: market.avg_s_responsible_sourcing,
    avgGAuditability: market.avg_g_auditability,
    avgGStrategy: market.avg_g_strategy,
    avgGTraining: market.avg_g_training,
    hotels: market.hotels, // Include hotels data
    fundamental_score: market.fundamental_score,
    fundamental_score_percentages: market.fundamental_score_percentages,
    fundamental_awarded_count: market.fundamental_awarded_count,
    fundamental_total: market.fundamental_total,
    fundamental_scored_hotels: market.fundamental_scored_hotels,
    fundamental_scored_hotels_percentage: market.fundamental_scored_hotels_percentage,
    fundamental_preferred_scored_hotels: market.fundamental_preferred_scored_hotels,
    fundamental_preferred_scored_hotels_percentage: market.fundamental_preferred_scored_hotels_percentage,
    fundamental_preferred_room_nights_scored: market.fundamental_preferred_room_nights_scored,
    fundamental_preferred_room_nights_scored_percentage   : market.fundamental_preferred_room_nights_scored_percentage,
  }));
};

export const useEnterpriseSummary = (quarterId?: string) => {
  const { error: showError } = useToast();
  const { selectedEnterprise } = useEnterprise();
  const enterpriseId = selectedEnterprise?.id;
  const [enterpriseSummary, setEnterpriseSummary] =
    useState<ProgramSummary | null>(null);
  const [marketSummary, setMarketSummary] = useState<MarketSummary | null>(
    null
  );
  const [allMarkets, setAllMarkets] = useState<any[]>([]);
  const [marketsListPagination, setMarketsListPagination] = useState<any>(null);
  const [sortBy, setSortBy] = useState<string>('Room nights booked');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoadingEnterprise, setIsLoadingEnterprise] = useState(false);
  const [isLoadingMarkets, setIsLoadingMarkets] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [enterpriseError, setEnterpriseError] = useState<string | null>(null);
  const [marketsError, setMarketsError] = useState<string | null>(null);

  // AbortController refs for cancelling ongoing requests
  const enterpriseAbortControllerRef = useRef<AbortController | null>(null);
  const marketsAbortControllerRef = useRef<AbortController | null>(null);

  // Synchronously mark as loading before the browser paints whenever the enterprise
  // or quarter changes, preventing a flash of "No data available" between renders.
  useLayoutEffect(() => {
    if (enterpriseId) {
      setIsLoadingEnterprise(true);
      setIsLoadingMarkets(true);
    }
  }, [quarterId, enterpriseId]);

  const fetchEnterpriseSummary = async () => {
    if (!enterpriseId) return;

    // Cancel previous request if it exists
    if (enterpriseAbortControllerRef.current) {
      enterpriseAbortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    enterpriseAbortControllerRef.current = new AbortController();
    const signal = enterpriseAbortControllerRef.current.signal;

    try {
      setIsLoadingEnterprise(true);
      setEnterpriseError(null);
      const response = await enterpriseAdminService.getEnterpriseSummary(
        quarterId,
        signal,
        enterpriseId
      );
      if (response) {
        setEnterpriseSummary(response);
      } else {
        setEnterpriseSummary(null);
      }
    } catch (error: any) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      console.error('Error fetching enterprise summary in hook: ', error);
      setEnterpriseError(error.message || 'Failed to fetch enterprise summary');
      // showError(error.message || 'Failed to fetch enterprise summary');
    } finally {
      // Only reset loading if this request wasn't aborted
      if (!signal.aborted) {
        setIsLoadingEnterprise(false);
      }
    }
  };

  const fetchMarketSummary = useCallback(
    async (
      filterSortBy?: string,
      page?: number,
      append: boolean = false,
      search?: string,
      isSearching: boolean = false,
      size?: number
    ) => {
      // Cancel previous request if it exists
      if (marketsAbortControllerRef.current) {
        marketsAbortControllerRef.current.abort();
      }

      if (!enterpriseId) return;

      // Create new AbortController for this request
      marketsAbortControllerRef.current = new AbortController();
      const signal = marketsAbortControllerRef.current.signal;

      // Don't fetch if quarterId is not valid
      // if (!quarterId || quarterId === '') {
      //   console.warn('fetchMarketSummary called without valid quarterId');
      //   return;
      // }
      
      try {
        if (append) {
          setIsLoadingMore(true);
        } else if (isSearching) {
          setIsSearchLoading(true);
        } else {
          setIsLoadingMarkets(true);
        }
        setMarketsError(null);
        const response = await enterpriseAdminService.getMarketsSummary({
          params: {
            sortBy: filterSortBy !== undefined ? filterSortBy : sortBy,
            page: page !== undefined ? page : currentPage,
            search: search !== undefined ? search : searchTerm,
            limit: size || pageSize,
          },
          quarterId,
          signal,
          enterpriseId,
        });
        if (response) {
          setMarketSummary(response?.data);
          setMarketsListPagination(response?.pagination || null);

          if (append && response?.data?.markets) {
            // Append new markets to existing ones (transform new data)
            // console.log(
            //   'Appending new markets to existing list',
            //   allMarkets.length
            // );
            setAllMarkets(prev => {
              const transformedNewMarkets = transformMarketData(
                response.data.markets,
                prev.length
              );
              return [...prev, ...transformedNewMarkets];
            });
          } else if (response?.data?.markets) {
            const transformedMarkets = transformMarketData(
              response.data.markets,
              0
            );
            setAllMarkets(transformedMarkets);
          } else {
            setAllMarkets([]);
          }
        }
      } catch (error: any) {
        // Ignore abort errors
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          return;
        }
        const errorMessage = 'Failed to fetch market summary';
        setMarketsError(errorMessage);
        // showError(errorMessage);
      } finally {
        // Only reset loading if this request wasn't aborted
        if (!signal.aborted) {
          setIsLoadingMarkets(false);
          setIsLoadingMore(false);
          setIsSearchLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quarterId, pageSize, enterpriseId]
  );

  const handleFilterChange = useCallback(
    (newSortBy: string) => {
      setSortBy(newSortBy);
      setCurrentPage(1);
      setAllMarkets([]); // Clear markets on filter change
      fetchMarketSummary(newSortBy, 1, false, searchTerm, false);
    },
    [searchTerm, fetchMarketSummary]
  );

  const handleSearchChange = useCallback(
    (newSearchTerm: string) => {
      setSearchTerm(newSearchTerm);
      setCurrentPage(1);
      // Don't clear markets during search - keep old data until new data arrives
      fetchMarketSummary(sortBy, 1, false, newSearchTerm, true);
    },
    [sortBy, fetchMarketSummary]
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setCurrentPage(1);
    // Don't clear markets when clearing search - keep old data until new data arrives
    fetchMarketSummary(sortBy, 1, false, '', false);
  }, [sortBy, fetchMarketSummary]);

  const loadMoreMarkets = useCallback(() => {
    if (marketsListPagination?.hasNextPage && !isLoadingMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchMarketSummary(sortBy, nextPage, true, searchTerm, false);
    }
  }, [currentPage, marketsListPagination, sortBy, isLoadingMore, searchTerm, fetchMarketSummary]);

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      setAllMarkets([]); // Clear markets on page change
      fetchMarketSummary(sortBy, page, false, searchTerm, false, pageSize);
    },
    [sortBy, searchTerm, pageSize, fetchMarketSummary]
  );

  const handleNextPage = useCallback(() => {
    if (marketsListPagination?.hasNextPage) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      setAllMarkets([]); // Clear markets on next page
      fetchMarketSummary(sortBy, nextPage, false, searchTerm, false);
    }
  }, [currentPage, marketsListPagination, sortBy, searchTerm, fetchMarketSummary]);

  const handlePrevPage = useCallback(() => {
    if (marketsListPagination?.hasPrevPage) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      setAllMarkets([]); // Clear markets on prev page
      fetchMarketSummary(sortBy, prevPage, false, searchTerm, false);
    }
  }, [currentPage, marketsListPagination, sortBy, searchTerm, fetchMarketSummary]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      setCurrentPage(1); // Reset to page 1 when page size changes
      setAllMarkets([]); // Clear markets
      fetchMarketSummary(sortBy, 1, false, searchTerm, false, newPageSize);
    },
    [sortBy, searchTerm, fetchMarketSummary]
  );

  useEffect(() => {
    // Only fetch if quarterId is defined AND not empty
    // This prevents unnecessary API calls when quarterId is being initialized
    // if (quarterId !== undefined) {
      fetchEnterpriseSummary();
      setCurrentPage(1);
      setSearchTerm(''); // Clear search on quarter change
      setAllMarkets([]); // Clear markets on quarter change
      fetchMarketSummary(sortBy, 1, false, '', false);

    // Cleanup function to abort ongoing requests when component unmounts or quarterId changes
    return () => {
      if (enterpriseAbortControllerRef.current) {
        enterpriseAbortControllerRef.current.abort();
      }
      if (marketsAbortControllerRef.current) {
        marketsAbortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quarterId, enterpriseId]);

  return {
    enterpriseSummary,
    marketSummary,
    allMarkets,
    marketsListPagination,
    currentPage,
    pageSize,
    sortBy,
    searchTerm,
    isLoadingEnterprise,
    isLoadingMarkets,
    isLoadingMore,
    isSearchLoading,
    enterpriseError,
    marketsError,
    handleFilterChange,
    handleSearchChange,
    handleClearSearch,
    handlePageChange,
    handleNextPage,
    handlePrevPage,
    handlePageSizeChange,
    loadMoreMarkets,
    refetchEnterpriseSummary: fetchEnterpriseSummary,
    refetchMarketSummary: fetchMarketSummary,
  };
};
